import { styled } from '@mui/material';
import Box from '@mui/material/Box';
import { omit } from 'lodash';
import { useEffect } from 'react';

import { ELayoutManagerMode } from '#shared';

import { useAppDispatch } from '../../redux-store';
import { blockSelectors, useSelectRegionsActive } from '../../slices';
import {
  useSelectWidgetManagerDraggingWidget,
  widgetManagerActions,
} from '../../widgetManager';
import { useLayoutManagerConfig } from '../lib';
import { useLayoutMode } from '../model';

import { DeleteZoneOverlay } from './DeleteZoneOverlay';
import { Region, type RegionProps } from './Region';
import { useToast } from './useToast';

const PREFIX = 'TSMED-LayoutManager';

export const layoutManagerClasses = {
  root: `${PREFIX}-root`,
  header: `${PREFIX}-header`,
  body: `${PREFIX}-body`,
  regions: `${PREFIX}-regions`,
};

const Root = styled(Box, {
  shouldForwardProp: p =>
    !['columns', 'areas', 'styleSlots'].includes(String(p)),
})<{
  columns: string;
  areas: string;
  styleSlots: LayoutManagerProps['styleSlots'];
}>(({ columns, areas, theme, styleSlots }) => ({
  [`&.${layoutManagerClasses.root}`]: {
    position: 'relative',
    width: '100%',
    height: 'var(--vh-full)',
    display: 'flex',
    gap: theme.spacing(1),
    flexDirection: 'column',
    ...styleSlots?.manager?.root,
  },
  [`.${layoutManagerClasses.header}`]: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 1),
    gap: theme.spacing(1),
    position: 'relative',
    zIndex: 100,
    ...styleSlots?.manager?.header,
  },
  [`.${layoutManagerClasses.body}`]: {
    width: '100%',
    container: 'layout-manager / size',
    ...styleSlots?.manager?.body,
  },
  [`.${layoutManagerClasses.regions}`]: {
    display: 'grid',
    gap: theme.spacing(1),
    padding: theme.spacing(1), // update width calculation if changed
    width: `calc(100% - 2 * ${theme.spacing(1)})`, // subtract left and right padding
    gridTemplateColumns: columns,
    gridTemplateAreas: areas,
    zoom: 'calc(min(100cqw / 100vw, 100cqh / 100vh))',
    height: 'var(--vh-full)',
    aspectRatio: 'calc(100vw / 100vh)',
    ...styleSlots?.manager?.regions,
  },
}));

export interface LayoutManagerProps {
  styleSlots?: {
    manager?: Partial<
      Record<keyof typeof layoutManagerClasses, React.CSSProperties>
    >;
  } & RegionProps['styleSlots'];
  slots?: {
    header?: boolean;
  };
  mode?: ELayoutManagerMode;
}

// Internal component with access to LayoutManager context
const LayoutContent = ({
  styleSlots,
  gridTemplateColumns,
  gridTemplateAreas,
  activeRegions,
  mode = ELayoutManagerMode.RENDER,
}: {
  styleSlots: LayoutManagerProps['styleSlots'];
  gridTemplateColumns: string;
  gridTemplateAreas: string;
  activeRegions: ReturnType<typeof useSelectRegionsActive>;
  slots?: LayoutManagerProps['slots'];
  mode?: ELayoutManagerMode;
}) => {
  const draggingWidget = useSelectWidgetManagerDraggingWidget();
  const dispatch = useAppDispatch();

  // Always call hook unconditionally (Rules of Hooks)
  const editingContext = useLayoutMode();

  const { showToast, ToastComponent } = useToast();

  // Sync mode to Redux so WidgetToolbar can detect VIEW/EDIT/RENDER mode
  useEffect(() => {
    dispatch(
      widgetManagerActions.setGeneralSettings({
        layoutManagerMode: mode,
      }),
    );
  }, [mode, dispatch]);

  // Derive readOnly from mode - no separate prop needed
  const readOnly = mode === ELayoutManagerMode.VIEW;

  // Only handle click outside in EDIT mode
  const handleRootClick = (e: React.MouseEvent) => {
    if (mode !== ELayoutManagerMode.EDIT) {
      return;
    }

    // Clear selections if clicked on root or regions container
    const target = e.target as HTMLElement;
    const isRootOrRegions =
      target === e.currentTarget ||
      target.classList.contains(layoutManagerClasses.regions) ||
      target.classList.contains(layoutManagerClasses.body);

    if (isRootOrRegions) {
      if (editingContext.selectedPaletteWidget) {
        editingContext.setSelectedPaletteWidget(null);
      }
      if (editingContext.selectedWidgetId) {
        editingContext.setSelectedWidgetId(null);
      }
    }
  };

  const regionStyleSlots = omit(styleSlots, 'manager');

  // Show delete zone only in EDIT mode when dragging or selecting
  const showDeleteZone = Boolean(
    mode === ELayoutManagerMode.EDIT &&
    (draggingWidget?.type === 'widget-instance' ||
      !!editingContext.selectedWidgetId),
  );

  // Handle tap-to-delete when widget is selected - only in EDIT mode
  const handleTapDelete = () => {
    if (mode !== ELayoutManagerMode.EDIT) {
      return;
    }

    const { selectedWidgetId, handleDeleteWidget, setSelectedWidgetId } =
      editingContext;

    if (selectedWidgetId) {
      // handleDeleteWidget in context now includes last-widget validation
      handleDeleteWidget(selectedWidgetId);
      setSelectedWidgetId(null);
      showToast('Widget removed', 'success');
    }
  };

  return (
    <Root
      className={layoutManagerClasses.root}
      columns={gridTemplateColumns}
      areas={gridTemplateAreas}
      styleSlots={styleSlots}
      onClick={handleRootClick}
    >
      {/* Delete zone overlay only in EDIT mode */}
      {mode === ELayoutManagerMode.EDIT && (
        <DeleteZoneOverlay
          visible={showDeleteZone}
          onDelete={handleTapDelete}
        />
      )}

      <Box className={layoutManagerClasses.body} onClick={handleRootClick}>
        <Box
          className={layoutManagerClasses.regions}
          gridTemplateColumns={gridTemplateColumns}
          gridTemplateAreas={gridTemplateAreas}
          onClick={handleRootClick}
          sx={{
            pointerEvents: readOnly ? 'none' : 'auto',
          }}
        >
          {activeRegions.map(({ id }) => (
            <Region
              key={id}
              region={id}
              area={id}
              mode={mode}
              styleSlots={regionStyleSlots}
            />
          ))}
        </Box>
      </Box>

      <ToastComponent />
    </Root>
  );
};

export const LayoutManager = ({
  styleSlots,
  mode,
  slots,
}: LayoutManagerProps) => {
  const activeRegions = useSelectRegionsActive();
  const hasNewCasesAside = blockSelectors.useSelectNewCasesBlocksVisible();
  const centerBonusPx = hasNewCasesAside ? 300 : 0;

  const { gridTemplateColumns, gridTemplateAreas } = useLayoutManagerConfig(
    Object.values(activeRegions).map(r => r.id),
    centerBonusPx,
  );

  return (
    <LayoutContent
      styleSlots={styleSlots}
      gridTemplateColumns={gridTemplateColumns}
      gridTemplateAreas={gridTemplateAreas}
      activeRegions={activeRegions}
      slots={slots}
      mode={mode}
    />
  );
};
